import PropTypes from 'prop-types';

import {
  Button,
  ButtonText,
  Checkbox,
  Fieldset,
  Input,
  PlusIcon,
  Radio,
  Select,
  TrashIcon,
} from '@utilities';

import { CONFIDENCE_LEVELS, FILTER_LOGIC, SOURCE_CATEGORY_ORDER } from '../../utilities/constants';

import styles from './_index.module.scss';

const SettingsPanel = ({
  baseThresholdInput,
  confidence,
  filterLogic,
  filterRows,
  filterSources,
  onAddFilterRow,
  onBaseThresholdBlur,
  onBaseThresholdChange,
  onConfidenceChange,
  onFilterLogicChange,
  onFilterRowSourceChange,
  onFilterRowValueToggle,
  onRemoveFilterRow,
}) => {
  const sourceOptionGroups = SOURCE_CATEGORY_ORDER.map((category) => ({
    label: category.label,
    options: filterSources
      .filter(({ category: sourceCategory }) => sourceCategory === category.id)
      .map(({ id, label }) => ({ label, value: id })),
  })).filter(({ options }) => options.length > 0);
  const sourceOptions = sourceOptionGroups.flatMap(({ options }) => options);

  return (
    <div className={styles['settings-panel']}>
      <section>
        <h4>Table Settings</h4>
        <Fieldset legend="Confidence level">
          {CONFIDENCE_LEVELS.map((level) => (
            <Radio
              isChecked={confidence === level}
              key={level}
              label={`${level}%`}
              name="crosstab-confidence"
              onChange={() => onConfidenceChange(level)}
              value={level}
            />
          ))}
        </Fieldset>
        <div className={styles['settings-panel-threshold']}>
          <label htmlFor="crosstab-base-threshold">Suppress significance when n {'<'}</label>
          <Input
            aria-label="Suppress significance testing for columns with fewer respondents than this"
            className={styles['settings-panel-threshold-input']}
            id="crosstab-base-threshold"
            min={1}
            onBlur={onBaseThresholdBlur}
            onChange={onBaseThresholdChange}
            type="number"
            value={baseThresholdInput}
          />
        </div>
      </section>

      <section>
        <h4>Base Filters</h4>
        <p className={styles['settings-panel-hint']}>
          Filters scope the respondents included in every table.
        </p>
        {filterRows.length > 1 && (
          <Fieldset legend="Combine filters with">
            {Object.values(FILTER_LOGIC).map((logic) => (
              <Radio
                isChecked={filterLogic === logic}
                key={logic}
                label={logic}
                name="crosstab-filter-logic"
                onChange={() => onFilterLogicChange(logic)}
                value={logic}
              />
            ))}
          </Fieldset>
        )}
        {filterRows.map((row) => {
          const source = filterSources.find(({ id }) => id === row.sourceId);

          return (
            <div className={styles['settings-panel-filter-row']} key={row.id}>
              <div className={styles['settings-panel-filter-row-header']}>
                <Select
                  aria-label="Filter attribute"
                  className={styles['settings-panel-filter-select']}
                  onChange={({ value }) => onFilterRowSourceChange(row.id, value)}
                  options={sourceOptionGroups}
                  placeholder="Select attribute"
                  value={sourceOptions.find(({ value }) => value === row.sourceId) ?? null}
                />
                <ButtonText
                  aria-label="Remove filter"
                  onClick={() => onRemoveFilterRow(row.id)}
                  title="Remove filter"
                >
                  <TrashIcon />
                </ButtonText>
              </div>
              {source && (
                <ul className={styles['settings-panel-filter-values']}>
                  {source.values.map(({ id, label }) => (
                    <li key={id}>
                      <Checkbox
                        id={`crosstab-filter-${row.id}-${id}`}
                        isChecked={row.values.includes(id)}
                        label={label}
                        onChange={() => onFilterRowValueToggle(row.id, id)}
                        title={label}
                        value={id}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
        <Button
          icon={<PlusIcon />}
          onClick={onAddFilterRow}
          text="Add filter"
          variant="secondary"
        />
      </section>
    </div>
  );
};

SettingsPanel.propTypes = {
  baseThresholdInput: PropTypes.string.isRequired,
  confidence: PropTypes.number.isRequired,
  filterLogic: PropTypes.oneOf(Object.values(FILTER_LOGIC)).isRequired,
  filterRows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      sourceId: PropTypes.string,
      values: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
  filterSources: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      values: PropTypes.array.isRequired,
    })
  ).isRequired,
  onAddFilterRow: PropTypes.func.isRequired,
  onBaseThresholdBlur: PropTypes.func.isRequired,
  onBaseThresholdChange: PropTypes.func.isRequired,
  onConfidenceChange: PropTypes.func.isRequired,
  onFilterLogicChange: PropTypes.func.isRequired,
  onFilterRowSourceChange: PropTypes.func.isRequired,
  onFilterRowValueToggle: PropTypes.func.isRequired,
  onRemoveFilterRow: PropTypes.func.isRequired,
};

export default SettingsPanel;
